import { Box, Button, IconButton, MenuItem, ToggleButtonGroup, alpha, styled } from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';

export const getToolbarAppButtonSx = (collapsed: boolean): SystemStyleObject<Theme> => ({
  height: '1.8125rem',
  minHeight: '1.8125rem',
  paddingTop: 0,
  paddingBottom: 0,
  ...(collapsed && { width: '1.8125rem', paddingLeft: 0, paddingRight: 0 }),
  '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
    fontSize: '1rem',
  },
});

export const GranularityToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  minWidth: 0,
  padding: '0.125rem',
  borderRadius: '2.25rem',
  backgroundColor: theme.palette.purple[50],

  '& button': {
    minWidth: '3.5rem',
    height: '1.4375rem',
    textTransform: 'none',
    borderRadius: '2.25rem',
    paddingY: 0,
    border: 'none !important',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1,
    color: theme.palette.grey[600],
  },

  '& .Mui-selected': {
    backgroundColor: `${theme.palette.background.paper} !important`,
    borderRadius: '2.25rem',
    color: `${theme.palette.grey[700]} !important`,
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.08)',
  },
}));

export const ToolbarRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'plain',
})<{ isModal?: boolean; seamless?: boolean; plain?: boolean }>(
  ({ theme, isModal, seamless, plain }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isModal ? '0.125rem' : plain ? 0 : '0 0.875rem',
    minHeight: isModal || plain ? 0 : '3.3125rem',
    backgroundColor: plain ? 'transparent' : theme.palette.background.paper,
    borderRadius: plain ? 0 : seamless ? '0.875rem 0.875rem 0 0' : '0.875rem',
    overflow: plain ? 'visible' : 'hidden',
    boxShadow: isModal || seamless || plain ? 'none' : '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
    marginBottom: seamless ? 0 : '1rem',
    flexShrink: 0,
    gap: '1.5rem',
  }),
);

export const ToolbarSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  minWidth: 0,
});

export const ButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
});

export const ActionButton = styled(Button)<{
  actionColor?: 'primary' | 'error';
  collapsed?: boolean;
  disabled?: boolean;
}>(({ theme, actionColor = 'primary', collapsed = false, disabled = false }) => {
  const colorExpanded =
    actionColor === 'error' ? theme.palette.red[500] : theme.palette.accent.main;
  const colorCollapsed = actionColor === 'error' ? theme.palette.red[500] : theme.palette.grey[600];

  const color = disabled ? theme.palette.grey[200] : collapsed ? colorCollapsed : colorExpanded;
  const borderColor = disabled
    ? theme.palette.grey[200]
    : collapsed
      ? colorCollapsed
      : colorExpanded;

  return {
    height: '1.6875rem',
    minWidth: collapsed ? '1.6875rem' : 'auto',
    padding: collapsed ? '0' : '0 0.75rem',
    borderRadius: '0.5rem',
    border: `0.0625rem solid ${borderColor}`,
    backgroundColor: 'transparent',
    color: color,
    fontFamily: 'Neue Frutiger World',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.6,
    textTransform: 'none',
    gap: collapsed ? '0' : '0.25rem',
    justifyContent: 'center',
    transition: 'all 0.25s ease',
    overflow: 'hidden',

    '&:hover': {
      backgroundColor:
        actionColor === 'error'
          ? alpha(theme.palette.red[500], 0.08)
          : collapsed
            ? alpha(theme.palette.grey[400], 0.08)
            : alpha(theme.palette.accent.main, 0.08),
      borderColor: borderColor,
    },

    '& .MuiButton-startIcon': {
      marginRight: 0,
      marginLeft: 0,
      transition: 'all 0.25s ease',

      '& svg': {
        fontSize: '1rem',
        color: color,
        transition: 'color 0.25s ease',
      },
    },

    '& .button-text': {
      display: 'inline-block',
      maxWidth: collapsed ? '0' : '12.5rem',
      opacity: collapsed ? 0 : 1,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: 'max-width 0.25s ease, opacity 0.2s ease',
    },
  };
});

export const ToolbarButton = styled(Button)<{ collapsed?: boolean }>(
  ({ theme, collapsed = false }) => ({
    height: '1.8125rem',
    minWidth: collapsed ? '1.8125rem' : 'auto',
    padding: collapsed ? '0' : '0.5rem 0.75rem',
    borderRadius: '0.625rem',
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.grey[600],
    fontFamily: 'Neue Frutiger World',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.6,
    textTransform: 'none',
    gap: collapsed ? '0' : '0.3125rem',
    justifyContent: 'center',
    border: 'none',
    transition: 'all 0.25s ease',
    overflow: 'hidden',

    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },

    '& .MuiButton-startIcon': {
      marginRight: 0,
      marginLeft: 0,
      transition: 'all 0.25s ease',

      '& svg': {
        fontSize: collapsed ? '1.25rem' : '1rem',
        color: theme.palette.grey[800],
        transition: 'font-size 0.25s ease',
      },
    },

    '& .MuiButton-endIcon': {
      marginRight: 0,
      marginLeft: collapsed ? '0' : '0.375rem',
      maxWidth: collapsed ? '0' : '1.25rem',
      opacity: collapsed ? 0 : 1,
      overflow: 'hidden',
      transition: 'all 0.25s ease',

      '& svg': {
        fontSize: '1.25rem',
        color: '#615E6F',
      },
    },

    '& .button-text': {
      display: 'inline-block',
      maxWidth: collapsed ? '0' : '12.5rem',
      opacity: collapsed ? 0 : 1,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: 'max-width 0.25s ease, opacity 0.2s ease',
    },
  }),
);

export const ToolbarIconButton = styled(IconButton)(({ theme }) => ({
  width: '1.8125rem',
  height: '1.8125rem',
  padding: '0.5rem',
  borderRadius: '0.625rem',
  color: theme.palette.grey[600],

  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },

  '& svg': {
    fontSize: '1rem',
  },
}));

export const ToolbarDivider = styled(Box)(({ theme }) => ({
  width: '0.0625rem',
  height: '1rem',
  backgroundColor: theme.palette.grey[300],
  borderRadius: '2.125rem',
}));

export const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  height: '1.8125rem',
  padding: '0.125rem',
  borderRadius: '0.625rem',
  backgroundColor: theme.palette.grey[50],
}));

export const ViewToggleButton = styled(IconButton)<{ active?: boolean }>(({ active }) => ({
  width: '1.5625rem',
  height: '1.5625rem',
  padding: 0,
  borderRadius: active ? '0.5rem' : '3.125rem',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
  color: '#615E6F',

  '&:hover': {
    backgroundColor: active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.04)',
  },

  '& svg': {
    fontSize: '1rem',
  },
}));

export const DropdownButton = styled(Button)<{ collapsed?: boolean }>(
  ({ theme, collapsed = false }) => ({
    height: '1.8125rem',
    minWidth: collapsed ? '1.8125rem' : '7.625rem',
    padding: collapsed ? '0' : '0.5rem 0.75rem',
    borderRadius: '0.625rem',
    backgroundColor: theme.palette.grey[50],
    color: '#615E6F',
    fontFamily: 'Neue Frutiger World',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.6,
    textTransform: 'none',
    gap: collapsed ? '0' : '0.3125rem',
    border: 'none',
    justifyContent: collapsed ? 'center' : 'space-between',
    transition: 'all 0.25s ease',
    overflow: 'hidden',

    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },

    '& .MuiButton-startIcon': {
      marginRight: 0,
      marginLeft: 0,
      transition: 'all 0.25s ease',

      '& svg': {
        fontSize: collapsed ? '1.25rem' : '1rem',
        color: '#615E6F',
        transition: 'font-size 0.25s ease',
      },
    },

    '& .MuiButton-endIcon': {
      marginRight: 0,
      marginLeft: collapsed ? '0' : 'auto',
      maxWidth: collapsed ? '0' : '1.25rem',
      opacity: collapsed ? 0 : 1,
      overflow: 'hidden',
      transition: 'all 0.25s ease',

      '& svg': {
        fontSize: '1.25rem',
        color: '#615E6F',
      },
    },

    '& .button-text': {
      display: 'inline-block',
      maxWidth: collapsed ? '0' : '12.5rem',
      opacity: collapsed ? 0 : 1,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: 'max-width 0.25s ease, opacity 0.2s ease',
    },
  }),
);

export const SearchInputContainer = styled(Box)<{ open?: boolean }>(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '1.8125rem',
  borderRadius: '0.625rem',
  backgroundColor: theme.palette.grey[50],
  overflow: 'hidden',
  transition: 'width 0.3s ease, opacity 0.3s ease',
  width: open ? '12.5rem' : '0',
  opacity: open ? 1 : 0,
}));

export const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: '100%',
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  padding: '0 0.75rem',
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.6,
  color: theme.palette.grey[800],

  '&::placeholder': {
    color: theme.palette.grey[500],
  },
}));

export const menuPaperProps = {
  sx: {
    backgroundColor: '#FFFFFF',
    borderRadius: '0.875rem',
    boxShadow: '0 0.0625rem 0.25rem 0.0625rem rgba(0, 0, 0, 0.1)',
    padding: '0.5rem !important',
    minWidth: '8.5rem',
    '& .MuiList-root': {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.125rem',
    },
  },
};

export const DropdownMenuItem = styled(MenuItem)<{ selected?: boolean }>(({ theme }) => ({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: `${theme.palette.grey[600]} !important`,
  padding: '0.25rem 0.5rem',
  borderRadius: '0.5rem',
  minHeight: 'unset',
  backgroundColor: 'transparent',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.625rem',

  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },

  '&.Mui-selected': {
    backgroundColor: 'transparent',
    color: `${theme.palette.grey[600]} !important`,

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

export const ColumnCheckbox = styled(Box)<{ checked?: boolean }>(({ theme, checked }) => ({
  width: '1rem',
  height: '1rem',
  borderRadius: '0.25rem',
  backgroundColor: checked ? theme.palette.accent.main : '#FFFFFF',
  border: checked ? 'none' : '1px solid #A4A4B6',
  boxShadow: checked
    ? '0 0.125rem 0.3125rem 0 rgba(103, 110, 118, 0.08), 0 0 0 0.0625rem rgba(103, 110, 118, 0.16), 0 0.0625rem 0.0625rem 0 rgba(0, 0, 0, 0.12)'
    : 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'all 0.15s ease',

  '& svg': {
    fontSize: '0.75rem',
    color: '#FEFEFE',
    opacity: checked ? 1 : 0,
    transition: 'opacity 0.15s ease',
  },
}));

export const ColumnMenuItem = styled(MenuItem)({
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.6,
  color: '#737085',
  padding: '0.25rem 0.5rem',
  borderRadius: '0.5rem',
  minHeight: 'unset',
  gap: '0.25rem',
  width: '100%',

  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
});
